import type { FromBasePlayerInput, FromBaseRow } from './from-base-context';
import { fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBasePlayerStageMetadata } from './from-base-player-stages';
import { createFromBasePlayerStageContext, constructPlayerThroughPostInventoryFromBase } from './from-base-player-stages';
import { createNestedPassiveFolder } from './nested-passive-fold';
import { createAbilityFinalizerGates } from './ability-finalizer-gates';
import { createPassivePermanentStatusCompiler } from './passive-permanent-status';
import { createCreatureEquippedCountsCompiler } from './creature-equipped-counts';
import type { CreatureEquippedCountsDependencies } from './creature-equipped-counts';
import { answerConditionContext, ConditionContextUnknown } from './condition-context';
import type { AbilityConditionDependencies } from './ability-conditions';
import type { ItemFieldRow } from './item-field-reader';
import { readInventoryField, readItemUuidSlot } from './inventory-raw-readers';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { createEquippedIntegerCompiler } from './equipped-integer-contribution';
import { createEnchantmentCompilationContext } from './enchantment-template-context';
import { createFormulaReadContext } from './formula-read-context';
import { readAbilityEntryId } from './inventory-ability-grant-helpers';

export interface FromBasePlayerGrantMetadata extends FromBasePlayerStageMetadata {
    defenseIntegerLayers: readonly string[];
    equippedCounts: Pick<CreatureEquippedCountsDependencies, 'naturalArmorPriority' | 'shieldArmorType'
        | 'pairedLimbForms' | 'engineFistMarkerId' | 'quadrupedLayer'>;
    equipmentSlots: CreatureEquippedCountsDependencies['equipmentSlots'];
}

/** One condition/identity context for the post-inventory construction windows. */
export function createFromBasePlayerGrantContext(base: FromBasePlayerInput, metadata: FromBasePlayerGrantMetadata, input: FromBaseRow,
    stage = 'nested-grants-override-prune') {
    // Lean stages omit a known empty tree; the held raw source distinguishes
    // that from a populated inventory whose construction is unavailable.
    const inventory = input.inventory ?? (base.inventory?.length === 0 ? [] : undefined);
    if (!Array.isArray(inventory)) throw new FromBaseInputMissing('constructed-inventory', base.entity_id);
    if (!Array.isArray(metadata.defenseIntegerLayers) || !metadata.equippedCounts)
        throw new FromBaseInputMissing('grant-recount-metadata', base.entity_id);
    const { common, definition } = createFromBasePlayerStageContext(base, metadata, inventory as FromBaseRow[]);
    const readers = createFormulaReadContext(common);
    const context = base.encounter_context;
    const conditionSource: AbilityConditionDependencies['conditionSource'] = (kind, args) => {
        try {
            return answerConditionContext(kind, args, { encounter: context ?? null,
                campaign: context && fromBaseRecord(context.campaign_arr) ? context.campaign_arr : null,
                value: readers.layerValue, entries: (row, key) => Object.values(readers.layerEntries(row, key)),
                abilityTypes: metadata.abilityTypes, equipmentSlots: metadata.equipmentSlots,
                definition: id => { const row = definition(id); return fromBaseRecord(row) ? row : null; },
                charges: (holder, item) => {
                    if (!Array.isArray(base.charges)) return undefined;
                    const row = base.charges.find(row => readItemUuidSlot(readers.layerValue(row, 'charge_holder')).toUpperCase() === holder.toUpperCase()
                        && readItemUuidSlot(readers.layerValue(row, 'item_id')).toUpperCase() === item.toUpperCase()
                        && Number(readers.layerValue(row, 'charge_deleted') ?? 0) === 0);
                    return row ? Number(readers.layerValue(row, 'charges') ?? 0) : null;
                } });
        } catch (error) {
            if (error instanceof ConditionContextUnknown) throw new FromBaseStageMissing(stage, `${error.kind}:${error.why}`);
            throw error;
        }
    };
    const deps = { ...common, equipmentSlots: metadata.equipmentSlots, vitalityStatusIds: metadata.vitalityStatusIds,
        skillEntries: metadata.skills, conditionSource, queryFullItem: definition };
    return { common, definition, readers, deps };
}

/** Native post-inventory windows, built from raw definitions and TS inventory.
 * The owning inventory/form gaps remain attached to the enclosing checkpoint. */
export function constructPlayerNestedGrantsFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerGrantMetadata, input: FromBaseRow) {
    const { common, definition, readers, deps } = createFromBasePlayerGrantContext(base, metadata, input);
    const gear = createEquippedIntegerCompiler({ ...common, metadata: metadata.preliminary, queryFullItem: definition });
    const entries = (row: ItemFieldRow | null, key: string) => Object.values(readers.layerEntries(row, key));
    const has = (row: ItemFieldRow | null, key: string) => readers.layerValue(row, key) !== null || phpJsonTruthy(entries(row, key));
    const counted = (host: ItemFieldRow, entry: ItemFieldRow, layer: string): boolean => {
        if (!['ac_mod_magical', 'ac_mod_non_magical', 'damage_mitigation'].includes(layer)) return false;
        const source = readItemUuidSlot(readers.layerValue(entry, 'source'));
        const nested = fromBaseRecord(entry) && fromBaseRecord(entry.item_arr) ? entry.item_arr : null;
        const passiveId = readItemUuidSlot(readers.layerValue(entry, 'ability_id') ?? readers.itemField(nested, 'item_id'));
        if (!fromBaseUuid(source) || !fromBaseUuid(passiveId)) return false;
        for (const row of gear.collect(host)) {
            if (phpJsonString(readInventoryField(row, 'inventory_id')).toUpperCase() !== source.toUpperCase()) continue;
            const computed = fromBaseRecord(row) && fromBaseRecord(row.computed) ? row.computed : {};
            const enchantment = fromBaseRecord(computed.enchantment) ? computed.enchantment : {};
            const abilities = fromBaseRecord(enchantment.abilities) ? enchantment.abilities : {};
            for (const passive of Object.values(abilities.passive_ability ?? {})) {
                if (!fromBaseRecord(passive) || readers.layerValue(passive, 'passive_conditions_active') === false) continue;
                const id = readAbilityEntryId(passive, { itemField: readers.itemField, resolveCarrierRef: definition });
                if (phpJsonString(id).toUpperCase() === passiveId.toUpperCase()) return true;
            }
            let carrier = row, template = readers.layerValue(row, 'enchantment_template_applied');
            if (!phpJsonTruthy(template)) {
                const itemId = readItemUuidSlot(readInventoryField(row, 'inventory_item_id'));
                carrier = fromBaseUuid(itemId) ? definition(itemId) ?? {} : {};
                template = readers.layerValue(carrier, 'enchantment_template_applied');
                if (phpJsonTruthy(template)) throw new FromBaseStageMissing('nested-grants-override-prune', `intrinsic-definition-finalization:${itemId}`);
            }
            if (!entries(carrier, layer).length) return false;
            const templateId = readItemUuidSlot(template);
            if (!fromBaseUuid(templateId)) return false;
            if (!metadata.inventoryMetadata || !base.enchantment_templates || !base.enchantment_template_enchantments)
                throw new FromBaseInputMissing('enchantment-template-sources', templateId);
            if (!base.enchantment_templates.some(raw => readItemUuidSlot(readers.layerValue(raw, 'enchantment_template_id')).toUpperCase() === templateId.toUpperCase()))
                throw new FromBaseInputMissing('enchantment-template', templateId);
            const context = createEnchantmentCompilationContext({ ...common,
                dynamicLayers: metadata.inventoryMetadata.dynamicLayers, spellCosts: metadata.inventoryMetadata.spellCosts,
                templateRows: id => base.enchantment_templates!.filter(raw => readItemUuidSlot(readers.layerValue(raw, 'enchantment_template_id')).toUpperCase() === id.toUpperCase()),
                memberRows: id => base.enchantment_template_enchantments!
                    .filter(raw => readItemUuidSlot(readers.layerValue(raw, 'enchantment_template_id')).toUpperCase() === id.toUpperCase())
                    .map(raw => {
                        const itemId = readItemUuidSlot(readers.layerValue(raw, 'enchantment_item_id'));
                        return { ...raw, item_name: fromBaseUuid(itemId) ? readers.itemField(definition(itemId), 'item_name') : null };
                    }),
                fullItem: definition });
            const built = context.buildTemplate(templateId, true);
            const nestedAbilities = fromBaseRecord(built) && fromBaseRecord(built.nested_abilities) ? built.nested_abilities : {};
            return Object.values(nestedAbilities.passive_ability ?? {}).some(raw => fromBaseRecord(raw) && fromBaseRecord(raw.item_arr)
                && !has(raw.item_arr, 'requires_equipped_item') && phpJsonString(readers.itemField(raw.item_arr, 'item_id')).toUpperCase() === passiveId.toUpperCase());
        }
        return false;
    };
    const nested = createNestedPassiveFolder({ ...deps,
        isAura: row => ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].every(key => readers.layerValue(row, key) !== null),
    })(input) as FromBaseRow;
    const gates = createAbilityFinalizerGates({ ...deps, defenseIntegerLayers: metadata.defenseIntegerLayers, gearPassiveCountedOnEquippedRow: counted });
    const overridden = gates.override(nested) as FromBaseRow;
    const pruned = gates.prune(overridden) as FromBaseRow;
    const permanent = createPassivePermanentStatusCompiler(deps).initialize(pruned) as FromBaseRow;
    const equipped = createCreatureEquippedCountsCompiler({ ...deps, ...metadata.equippedCounts })(permanent) as FromBaseRow;
    const computed = equipped.computed as FromBaseRow;
    computed._cache = { ...computed._cache as FromBaseRow, gear_senses_unmerged_v: 1 };
    return { nested, overridden, pruned, permanent, equipped };
}

export function constructPlayerThroughNestedGrantsFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerGrantMetadata) {
    const post = constructPlayerThroughPostInventoryFromBase(base, metadata);
    if (post.kind !== 'constructed-prefix') return post;
    try {
        const stages = constructPlayerNestedGrantsFromBase(base, metadata, post.row as FromBaseRow);
        return { ...post, row: stages.equipped,
            completedStages: [...post.completedStages, 'nested-grants-override-prune'],
            missingStages: post.missingStages.filter(stage => stage !== 'nested-grants-override-prune') };
    } catch (error) {
        if (error instanceof FromBaseInputMissing) return { kind: 'missing-input' as const, stage: 'nested-grants-override-prune', dependency: error.dependency, id: error.id };
        if (error instanceof FromBaseStageMissing) return { kind: 'missing-stage' as const, stage: error.stage, detail: error.detail };
        throw error;
    }
}
